import { useState, useEffect, useCallback, useRef } from 'react';
import { getDashboard, getRecentActivities, getReservations, updateReservation, getSettings } from '../utils/api';
import { closeModalWithAnimation } from '../utils/modalHelpers';
import AdminPageSkeleton from './AdminPageSkeleton';
import Error500 from '../pages/errors/Error500';
import { formatPhoneNumber } from '../utils/formatters';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newActivityIds, setNewActivityIds] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingReservations, setPendingReservations] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [activityCardHeight, setActivityCardHeight] = useState(null);
  const dashboardMainRef = useRef(null);

  const loadDashboard = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      
      const adminUser = localStorage.getItem('admin_user');
      const csrfToken = localStorage.getItem('csrf_token');
      
      if (!adminUser || !csrfToken) {
        console.error('Missing session data:', { hasUser: !!adminUser, hasToken: !!csrfToken });
        setError('Nie ste prihlásený. Presmerovávam na prihlásenie...');
        setTimeout(() => {
          window.location.href = '/admin/login';
        }, 1000);
        return;
      }
      
      const response = await getDashboard();
      setDashboardData(response.data.data);
      setError(null);
      setServerError(false);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
        return;
      }
      if (!silent) {
        setError('Nepodarilo sa načítať dashboard dáta. Skúste to prosím neskôr.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const loadRecentActivities = useCallback(async () => {
    try {
      const adminUser = localStorage.getItem('admin_user');
      if (!adminUser) {
        console.error('Missing session data in loadRecentActivities');
        return;
      }
      
      const response = await getRecentActivities();
      const activities = response.data.data || [];
      
      // Format activities from backend
      const formattedActivities = activities.map(act => ({
        id: act.activity_id,
        reservationId: act.reservation_id,
        type: act.type,
        cele_meno: act.cele_meno,
        nazov_sluzby: act.nazov_sluzby,
        stav: act.stav,
        timestamp: act.timestamp,
        upravil_id: act.upravil_id,
        upravil_meno: act.upravil_meno,
        data: act
      }));
      
      setRecentActivities(prevActivities => {
        if (prevActivities.length > 0) {
          const oldIds = new Set(prevActivities.map(r => r.id));
          const newIds = formattedActivities.filter(r => !oldIds.has(r.id)).map(r => r.id);
          
          if (newIds.length > 0) {
            setNewActivityIds(newIds);
            setTimeout(() => setNewActivityIds([]), 3000);
          }
        }
        return formattedActivities;
      });
    } catch (err) {
      console.error('Failed to load activities:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
      }
    }
  }, []);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadDashboard(true),
        loadRecentActivities()
      ]);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  }, [loadDashboard, loadRecentActivities]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await getSettings();
        const data = response.data.data || response.data;
        const settings = data.settings || {};
        
        if (settings.dashboard_refresh_interval !== undefined) {
          setRefreshInterval(settings.dashboard_refresh_interval);
        }
      } catch (err) {
        console.error('Failed to load settings for dashboard:', err);
      }
    };
    
    loadSettings();
  }, []);

  useEffect(() => {
    const adminUser = localStorage.getItem('admin_user');
    if (adminUser) {
      try {
        const userData = JSON.parse(adminUser);
        const fullName = userData.cele_meno || userData.prihlasovacie_meno || 'Admin';
        const firstName = fullName.trim().split(/\s+/)[0];
        setLoggedInUser(firstName);
      } catch (err) {
        setLoggedInUser('Admin');
      }
    }
    
    loadDashboard();
    loadRecentActivities();

    if (refreshInterval > 0) {
      const intervalId = setInterval(() => {
        refreshData();
      }, refreshInterval * 1000);

      return () => clearInterval(intervalId);
    }
  }, [loadDashboard, loadRecentActivities, refreshData, refreshInterval]);

  useEffect(() => {
    let animationFrameId = null;
    let resizeObserver = null;

    const updateActivityCardHeight = () => {
      if (window.innerWidth <= 1200) {
        setActivityCardHeight(null);
        return;
      }

      const mainColumn = dashboardMainRef.current;
      if (!mainColumn) {
        return;
      }

      const nextHeight = Math.ceil(
        Math.max(mainColumn.scrollHeight, mainColumn.getBoundingClientRect().height)
      );

      setActivityCardHeight((currentHeight) => (
        currentHeight === nextHeight ? currentHeight : nextHeight
      ));
    };

    const scheduleHeightUpdate = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(updateActivityCardHeight);
    };

    scheduleHeightUpdate();

    if (dashboardMainRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleHeightUpdate);
      resizeObserver.observe(dashboardMainRef.current);
    }

    window.addEventListener('resize', scheduleHeightUpdate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      window.removeEventListener('resize', scheduleHeightUpdate);
    };
  }, [loading, dashboardData]);

  const handlePendingClick = async () => {
    try {
      const response = await getReservations();
      const allReservations = response.data.data || [];
      const pending = allReservations.filter(r => r.stav === 'pending');
      setPendingReservations(pending);
      setShowPendingModal(true);
    } catch (err) {
      console.error('Failed to load pending reservations:', err);
    }
  };

  const handleUpdateStatus = async (reservationId, newStatus) => {
    setProcessingId(reservationId);
    try {
      const adminUser = localStorage.getItem('admin_user');
      let adminUserId = null;
      if (adminUser) {
        try {
          const userData = JSON.parse(adminUser);
          adminUserId = userData.id;
        } catch (err) {
          console.error('Failed to parse admin user:', err);
        }
      }
      
      await updateReservation(reservationId, { 
        stav: newStatus,
        upravil_id: adminUserId 
      });
      await Promise.all([loadDashboard(true), loadRecentActivities()]);
      setPendingReservations(prev => {
        const updated = prev.filter(r => r.id !== reservationId);
        
        if (updated.length === 0) {
          setTimeout(() => {
            closeModalWithAnimation(() => setShowPendingModal(false));
          }, 1000);
        }
        
        return updated;
      });
    } catch (err) {
      console.error('Failed to update reservation:', err);
      setError('Nepodarilo sa aktualizovať rezerváciu');
    } finally {
      setProcessingId(null);
    }
  };

  const getTodayReservations = () => {
    if (!dashboardData || !dashboardData.today_reservations) return [];
    
    return dashboardData.today_reservations
      .sort((a, b) => a.cas_rezervacie.localeCompare(b.cas_rezervacie));
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;

    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (mins < 1) return 'Práve teraz';
    if (mins < 60) return `Pred ${mins} ${mins === 1 ? 'minútou' : 'minútami'}`;
    if (hours < 24) return `Pred ${hours} ${hours === 1 ? 'hodinou' : 'hodinami'}`;
    if (days < 7) return `Pred ${days} ${days === 1 ? 'dňom' : 'dňami'}`;
    if (weeks < 5) return `Pred ${weeks} ${weeks === 1 ? 'týždňom' : 'týždňami'}`;
    if (months < 12) return `Pred ${months} ${months === 1 ? 'mesiacom' : 'mesiacmi'}`;

    return date.toLocaleDateString('sk-SK');
  };

  if (loading) {
    return (
      <AdminPageSkeleton
        title="Váš prehľad"
        subtitle="Načítavam súhrn vašej práce a posledné aktivity"
        variant="dashboard"
        showAction={false}
      />
    );
  }

  if (serverError) {
    return <Error500 />;
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
        {/* <button onClick={loadDashboard} className="btn btn-sm btn-primary mt-3">
          Skúsiť znova
        </button> */}
      </div>
    );
  }

  const stats = dashboardData || {};
  const getTrendDisplay = (trend) => {
    const percentage = Math.abs(Number(trend?.percentage ?? 0));
    const direction = trend?.direction || 'neutral';

    return {
      className: direction === 'down' ? 'trend-down' : direction === 'up' ? 'trend-up' : 'trend-neutral',
      icon: direction === 'down' ? '↓' : direction === 'up' ? '↑' : '•',
      text: `${direction === 'up' ? '+' : direction === 'down' ? '-' : ''}${percentage}%`
    };
  };

  const getNextWorkingDayLabel = () => {
    const nextWorkingDay = stats.next_working_day;

    if (!nextWorkingDay?.date) {
      return 'Rezervácie zajtra';
    }

    if (nextWorkingDay.days_ahead === 1) {
      return 'Rezervácie zajtra';
    }

    const dayLabels = {
      0: 'Rezervácie v nedeľu',
      1: 'Rezervácie v pondelok',
      2: 'Rezervácie v utorok',
      3: 'Rezervácie v stredu',
      4: 'Rezervácie vo štvrtok',
      5: 'Rezervácie v piatok',
      6: 'Rezervácie v sobotu'
    };

    return dayLabels[nextWorkingDay.day_of_week] || 'Rezervácie najbližší pracovný deň';
  };

  const todayTrend = getTrendDisplay(stats.trends?.today);
  const nextWorkingDayTrend = getTrendDisplay(stats.trends?.next_working_day);
  const completedWeekTrend = getTrendDisplay(stats.trends?.completed_week);
  const monthTrend = getTrendDisplay(stats.trends?.month_total);

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div>
          <h2 className="dashboard-title">Prehľad vašej práce, {loggedInUser} </h2>
          <p className="dashboard-subtitle">
            Radi vás opäť vidíme! Pozrite si súhrn vašej práce.
            {isRefreshing && <span className="refresh-indicator-inline"> Zisťujú sa nové dáta...</span>}
          </p>
        </div>
      </div>

      {/* KPI Cards - Modern SaaS Style */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-label">Rezervácie dnes</span>
          </div>
          <div className="kpi-value">{stats.today_count || 0}</div>
          <div className={`kpi-trend ${todayTrend.className}`}>
            <span className="trend-icon">{todayTrend.icon}</span>
            <span className="trend-text">{todayTrend.text} vs. včera</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-label">{getNextWorkingDayLabel()}</span>
          </div>
          <div className="kpi-value">{stats.next_working_day_count ?? stats.tomorrow_count ?? 0}</div>
          <div className={`kpi-trend ${nextWorkingDayTrend.className}`}>
            <span className="trend-icon">{nextWorkingDayTrend.icon}</span>
            <span className="trend-text">{nextWorkingDayTrend.text} vs. minulý týždeň</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-label">Dokončené tento týždeň</span>
          </div>
          <div className="kpi-value">{stats.completed_week_count || 0}</div>
          <div className={`kpi-trend ${completedWeekTrend.className}`}>
            <span className="trend-icon">{completedWeekTrend.icon}</span>
            <span className="trend-text">{completedWeekTrend.text} vs. minulý týždeň</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-label">Celkom tento mesiac</span>
          </div>
          <div className="kpi-value">{stats.month_stats?.total || 0}</div>
          <div className={`kpi-trend ${monthTrend.className}`}>
            <span className="trend-icon">{monthTrend.icon}</span>
            <span className="trend-text">{monthTrend.text} vs. minulý mesiac</span>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Left Column */}
        <div className="dashboard-main" ref={dashboardMainRef}>
          {/* Total Stats - Unified Card */}
          {stats.total_stats && (
            <div className="stats-card month-stats">
              <h3 className="card-title">Štatistiky (celkovo)</h3>
              <div className="stats-grid-4x2">
                <div className="stat-item">
                  <div className="stat-item-label">Celkom</div>
                  <div className="stat-item-value">{stats.total_stats.total}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-item-label">Potvrdené</div>
                  <div className="stat-item-value stat-value-info">{stats.total_stats.confirmed}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-item-label">Zrušené</div>
                  <div className="stat-item-value stat-value-danger">{stats.total_stats.cancelled}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-item-label">Dokončené</div>
                  <div className="stat-item-value stat-value-success">{stats.total_stats.completed}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-item-label">Priemerný denný počet</div>
                  <div className="stat-item-value">{stats.month_stats.avg_per_day || '0'}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-item-label">Aktívni administrátori (10 min)</div>
                  <div className="stat-item-value stat-value-highlight">{stats.active_admins_count ?? 0}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-item-label">Blokované časy</div>
                  <div className="stat-item-value">{stats.blocked_times_count || 0}</div>
                </div>
                <div 
                  className="stat-item stat-item-clickable" 
                  onClick={handlePendingClick}
                  style={{cursor: 'pointer'}}
                  title="Kliknutím zobraziť nepotvrdené rezervácie"
                >
                  <div className="stat-item-label">Nepotvrdené rezervácie</div>
                  <div className="stat-item-value stat-value-warning">{stats.total_stats?.pending || 0}</div>
                </div>
              </div>
            </div>
          )}

          {/* Today's Timeline - Mini Calendar */}
          <div className="stats-card today-schedule">
            <div className="card-header-flex">
              <h3 className="card-title">Dnešný rozvrh</h3>
              <span className="card-subtitle">{new Date().toLocaleDateString('sk-SK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(/^(.+?)\s/, '$1, ')}</span>
            </div>
            <div className="timeline-container">
              {getTodayReservations().length > 0 ? (
                <div className="timeline">
                  {getTodayReservations().map((reservation, index) => {
                    const time = reservation.cas_rezervacie;
                    const statusClass = reservation.stav === 'confirmed' ? 'timeline-confirmed' : reservation.stav === 'pending' ? 'timeline-pending' : reservation.stav === 'cancelled' ? 'timeline-cancelled' : 'timeline-default';
                    
                    return (
                      <div key={index} className={`timeline-item ${statusClass}`}>
                        <div className="timeline-time">{time}</div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                          <div className="timeline-name">{reservation.cele_meno}</div>
                          <div className="timeline-service">{reservation.nazov_sluzby}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="timeline-empty">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <p>Dnes žiadne rezervácie</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Activity Stream */}
        <div className="dashboard-sidebar">
          <div
            className="stats-card activity-card"
            style={activityCardHeight ? { height: `${activityCardHeight}px`, maxHeight: `${activityCardHeight}px` } : undefined}
          >
            <h3 className="card-title">Posledné aktivity</h3>
            <div className="activity-stream">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity, index) => {
                  const isNew = newActivityIds.includes(activity.id);
                  
                  const getActivityText = (act) => {
                    const adminUser = localStorage.getItem('admin_user');
                    let adminName = 'Admin';
                    if (adminUser) {
                      try {
                        const userData = JSON.parse(adminUser);
                        const fullName = userData.cele_meno || userData.prihlasovacie_meno || 'Admin';
                        adminName = fullName.trim().split(/\s+/)[0];
                      } catch (err) {
                        adminName = 'Admin';
                      }
                    }

                    const activityAdminName = act.upravil_meno
                      ? act.upravil_meno.trim().split(/\s+/)[0]
                      : adminName;
                    
                    switch(act.type) {
                      case 'created':
                        return <><strong>{act.cele_meno}</strong> vytvoril rezerváciu na <strong>{act.nazov_sluzby || 'službu'}</strong></>;
                      case 'pending':
                        return <><strong>{act.cele_meno}</strong> vytvoril rezerváciu na <strong>{act.nazov_sluzby || 'službu'}</strong>. Potvrďte ju.</>;
                      case 'confirmed':
                        return <><strong>{activityAdminName}</strong> potvrdil rezerváciu od <strong>{act.cele_meno}</strong> na <strong>{act.nazov_sluzby || 'službu'}</strong></>;
                      case 'updated':
                        if (act.type === 'updated' && (act.updated_by_admin || act.upravil_id)) {
                          return <><strong>{activityAdminName}</strong> upravil rezerváciu od <strong>{act.cele_meno}</strong> na <strong>{act.nazov_sluzby || 'službu'}</strong></>;
                        }
                        return <><strong>{act.cele_meno}</strong> upravil rezerváciu na <strong>{act.nazov_sluzby || 'službu'}</strong></>;
                      case 'cancelled':
                        return <><strong>{act.cele_meno}</strong> zrušil rezerváciu na <strong>{act.nazov_sluzby || 'službu'}</strong></>;
                      case 'cancelled_by_admin':
                        return <><strong>{activityAdminName}</strong> zrušil rezerváciu od <strong>{act.cele_meno}</strong> na <strong>{act.nazov_sluzby || 'službu'}</strong></>;
                      default:
                        return <><strong>{act.cele_meno}</strong> - <strong>{act.nazov_sluzby || 'služba'}</strong></>;
                    }
                  };
                  
                  const getActivityTypeColor = (type) => {
                    switch(type) {
                      case 'created': return 'activity-warning';
                      case 'pending': return 'activity-warning';
                      case 'confirmed': return 'activity-success';
                      case 'updated': return 'activity-info';
                      case 'cancelled': return 'activity-danger';
                      case 'cancelled_by_admin': return 'activity-danger';
                      default: return '';
                    }
                  };
                  
                  const getActivityTypeIcon = (type) => {
                    switch(type) {
                      case 'created':
                        return (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2Z"/>
                          </svg>
                        );
                      case 'pending':
                        return (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                          </svg>
                        );
                      case 'confirmed':
                        return (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                          </svg>
                        );
                      case 'updated':
                        return (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                          </svg>
                        );
                      case 'cancelled':
                        return (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                          </svg>
                        );
                      case 'cancelled_by_admin':
                        return (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                          </svg>
                        );
                      default:
                        return (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <circle cx="8" cy="8" r="2"/>
                          </svg>
                        );
                    }
                  };
                  
                  return (
                    <div key={activity.id} className={`activity-item ${getActivityTypeColor(activity.type)} ${isNew ? 'activity-new' : ''}`}>
                      <div className="activity-icon">{getActivityTypeIcon(activity.type)}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          {getActivityText(activity)}
                        </div>
                        <div className="activity-time">{getTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="activity-empty">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                    <path d="M9 14h6"></path>
                    <path d="M9 18h6"></path>
                  </svg>
                  <p>Zatiaľ žiadne aktivity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal pre nepotvrdené rezervácie */}
      {showPendingModal && (
        <div className="modal-overlay" onClick={() => closeModalWithAnimation(() => setShowPendingModal(false))}>
          <div className="modal-content modal-pending-confirmation" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nepotvrdené rezervácie</h2>
              <button className="modal-close" onClick={() => closeModalWithAnimation(() => setShowPendingModal(false))}>×</button>
            </div>
            <div className="modal-body p-0">
              {pendingReservations.length > 0 ? (
                <div className="pending-confirmation-list">
                  {pendingReservations.map((reservation) => (
                    <div key={reservation.id} className="pending-confirmation-card">
                      <div className="pending-card-header">
                        <div className="customer-name-badge">
                          <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                          </svg>
                          <strong>{reservation.cele_meno}</strong>
                        </div>
                        <span className="badge badge-warning">Nepotvrdené</span>
                      </div>
                      
                      <div className="pending-card-details">
                        <div className="detail-primary">
                          <div className="detail-item">
                            <span className="detail-label">Služba</span>
                            <span className="detail-value">{reservation.nazov_sluzby}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Termín</span>
                            <span className="detail-value">
                              {new Date(reservation.datum_rezervacie).toLocaleDateString('sk-SK', { day: 'numeric', month: 'long', year: 'numeric' })} • {reservation.cas_rezervacie}
                            </span>
                          </div>
                        </div>
                        
                        <div className="detail-secondary">
                          <div className="detail-row">
                            <div className="detail-item">
                              <svg className="detail-icon" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555ZM0 4.697v7.104l5.803-3.558L0 4.697ZM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757Zm3.436-.586L16 11.801V4.697l-5.803 3.546Z"/>
                              </svg>
                              <span className="detail-value">{reservation.email}</span>
                            </div>
                            <div className="detail-item">
                              <svg className="detail-icon" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
                              </svg>
                              <span className="detail-value">{formatPhoneNumber(reservation.telefon)}</span>
                            </div>
                          </div>
                          <div className="detail-row">
                            <div className="detail-item">
                              <svg className="detail-icon" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                              </svg>
                              <span className="detail-value">{reservation.adresa}</span>
                            </div>
                            <div className="detail-item">
                              <svg className="detail-icon" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z"/>
                                <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                              </svg>
                              <span className="detail-value">{reservation.rezervacne_id || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="pending-card-actions">
                        <button
                          className="btn-confirm-primary"
                          onClick={() => handleUpdateStatus(reservation.id, 'confirmed')}
                          disabled={processingId === reservation.id}
                        >
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                          </svg>
                          Potvrdiť rezerváciu
                        </button>
                        <button
                          className="btn-cancel-link"
                          onClick={() => handleUpdateStatus(reservation.id, 'cancelled')}
                          disabled={processingId === reservation.id}
                        >
                          Zrušiť rezerváciu
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"></path>
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                  </svg>
                  <h3>Už žiadne nepotvrdené rezervácie</h3>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
